/**
 * Legacy lander hero: nudges the floating overlay horizontally so it stays
 * within the viewport with a fixed margin. Uses the smallest shift from the
 * theme’s `left` position: no movement when it already fits; otherwise clamps
 * to [margin, viewport − margin − width].
 */
(function () {
  if (window.__legacyLanderHeroOverlayInit) return;
  window.__legacyLanderHeroOverlayInit = true;

  const VIEWPORT_MARGIN_PX = 24;
  const SECTION_SELECTOR = '.shopify-section--legacy-lander-hero-image';
  const OVERLAY_SELECTOR = '.legacy-lander-hero-floating-overlay';

  /** @type {Map<Element, { overlay: Element, resizeObserver: ResizeObserver | null }>} */
  const registrations = new Map();

  let rafId = null;

  function scheduleClampAll() {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      for (const { overlay } of registrations.values()) {
        clampOverlayHorizontal(overlay);
      }
    });
  }

  /** Horizontal translate already applied (px), from computed style. */
  function appliedTranslateXPx(overlay) {
    const raw = getComputedStyle(overlay).transform;
    if (!raw || raw === 'none') return 0;
    return new DOMMatrix(raw).m41;
  }

  function clampOverlayHorizontal(overlay) {
    const sectionEl = overlay.closest(SECTION_SELECTOR);
    if (!sectionEl) return;
    if (window.getComputedStyle(overlay).display === 'none') return;

    const rect = overlay.getBoundingClientRect();
    if (rect.width < 1 && rect.height < 1) return;

    const width = rect.width;
    const viewportWidth = window.innerWidth;
    const correctionX = appliedTranslateXPx(overlay);
    const naturalLeft = rect.left - correctionX;

    const minLeft = VIEWPORT_MARGIN_PX;
    const maxLeft = viewportWidth - VIEWPORT_MARGIN_PX - width;

    let targetLeft = naturalLeft;
    if (maxLeft >= minLeft) {
      if (targetLeft < minLeft) targetLeft = minLeft;
      if (targetLeft > maxLeft) targetLeft = maxLeft;
    } else {
      targetLeft = minLeft;
    }

    const offsetX = targetLeft - naturalLeft;
    overlay.style.transform = `translateX(${offsetX}px)`;
  }

  function disconnectSection(sectionEl) {
    const entry = registrations.get(sectionEl);
    if (entry?.resizeObserver) {
      entry.resizeObserver.disconnect();
    }
    registrations.delete(sectionEl);
  }

  function registerSection(sectionEl) {
    disconnectSection(sectionEl);

    const overlay = sectionEl.querySelector(OVERLAY_SELECTOR);
    if (!overlay) return;

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleClampAll);
      resizeObserver.observe(overlay);
      const heroWrap = sectionEl.querySelector('.banner-image__hero-wrap');
      if (heroWrap) resizeObserver.observe(heroWrap);
    }

    registrations.set(sectionEl, { overlay, resizeObserver });
    clampOverlayHorizontal(overlay);

    const img = overlay.querySelector('img');
    if (img && !img.complete) {
      img.addEventListener('load', scheduleClampAll, { once: true, passive: true });
    }
  }

  function scanDocument() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(registerSection);
  }

  window.addEventListener('resize', scheduleClampAll, { passive: true });
  window.addEventListener('orientationchange', scheduleClampAll, { passive: true });

  document.addEventListener('shopify:section:load', (event) => {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;
    const sectionEl = document.getElementById(`shopify-section-${sectionId}`);
    if (sectionEl?.matches(SECTION_SELECTOR)) {
      registerSection(sectionEl);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const target = event.target;
    if (target instanceof Element && target.matches(SECTION_SELECTOR)) {
      disconnectSection(target);
      return;
    }
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;
    const sectionEl = document.getElementById(`shopify-section-${sectionId}`);
    if (sectionEl?.matches(SECTION_SELECTOR)) {
      disconnectSection(sectionEl);
    }
  });

  scanDocument();
})();
